/* eslint-disable max-lines -- a flat catalogue of independent, prop-driven leaf views; each is small and reads top-to-bottom, and splitting further would scatter one cohesive presentation layer across files for no readability gain */
/**
 * Evidence-styled presentation primitives for the project timeline.
 *
 * These are the leaf views the `renderProjectItem` dispatcher composes —
 * pure, prop-driven, no harness data coupling beyond the wire types.
 * Kept separate from the dispatcher so the per-`ChatItem` routing logic
 * reads on its own. The look mirrors main's `ProjectChatMessage`
 * `BlockRenderer` so the migration preserves the project chat feel.
 */
import {
  ChatObjectReference,
  RefMarkdownElement,
} from '@/composition/hosts/chat-object-reference';
import { useIsWorkSourceCitationChipsEnabled } from '@/features/flags/work-source-citation-chips-flag';
import { cn } from '@/lib/cn';
import { reactQueryClient } from '@/utils/reactQueryClient';
import {
  AttachmentBlockRenderer,
  ChatCiteChip,
  createSourceGroundedCitationComponent,
  RoutineOutcomeRow,
  SessionMetaRow,
  SourceGroundedReferencesFooter,
  useAvailableWorkflowSkillsWithWorkflows,
  type ChatCiteChipProps,
  type RenderCitationPopover,
} from '@heidi/agents';
import { splitRefSegments, type AttachmentBlock } from '@heidi/agents/contracts';
import {
  artifactOpenLabel,
  isComposerContextPreamble,
  projectTimelineBlocksMessages as m,
  parseUserPreamble,
  prepareAssistantMarkdown,
  prepareSourceGroundedContent,
  readSessionAnswerMeta,
  referencesById,
  splitRoutineTurnBody,
  useCachedWorkflowByInstructions,
  userMessageRendersBubble,
  type Artifact,
  type BlockStatus,
  type ParsedUserMessage,
  type PreparedSourceGroundedContent,
  type ResolvedClarification,
  type SourceGroundedReference,
  type SourceGroundedSessionTabs,
} from '@heidi/agents/data';
import {
  createResponseProse,
  mermaidConfig,
  MermaidStreamingContext,
  normalizeHeadingDepth,
  responseBodyTextClass,
  responseProseClass,
  responseQuestionWidthClass,
  shikiTheme,
  streamdownPlugins,
  streamdownRehypePlugins,
  useProseDensity,
  type MarkdownComponents,
  type ProseDensity,
} from '@heidi/answer-kit';
import { Button } from '@heidi/design-system/button';
import {
  CitationPopover,
  CitationPopoverContent,
  CitationPopoverTrigger,
} from '@heidi/design-system/citation-popover';
import { Routines } from '@heidi/design-system/icons';
import {
  TypographyCaption,
  TypographyP,
  TypographyP2,
} from '@heidi/design-system/typography';
import type { NavigatePort } from '@heidi/runtime/shell';
import { TabType } from '@heidi/session/contracts';
import { useQuerySessionById } from '@heidi/session/data';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronRight,
  CircleCheckBig,
  CircleStop,
  FileText,
  ListChecks,
  Loader2,
  PanelRight,
  type LucideIcon,
} from 'lucide-react';
import { useReducedMotion } from 'motion/react';
import Router from 'next/router';
import { Fragment, useMemo, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Streamdown, type AnimateOptions } from 'streamdown';
import { ClarificationAnswerTemplateLabel } from '../components/TemplatePickerList';

/** Status the tool blocks render against (mapped from `ToolUseStatus`). */
export type { BlockStatus };

// Everything typographic about assistant prose — body size, weights, the
// heading ladder, line heights and block spacing — comes from
// `@heidi/answer-kit`, the same system Evidence renders
// from. Work used to carry a bare `text-sm` wrapper here with NO heading map at
// all, so markdown headings fell through to Streamdown's defaults and a Work
// transcript that embedded an Evidence answer showed two body sizes.
//
// What stays here is the non-typographic chrome: link colour (Work uses the
// active token, Evidence underlines a tracked link) and the code/pre surfaces.
const ASSISTANT_MARKDOWN_CLASS = cn(
  'w-full min-w-0 text-text-primary',
  '[&_a]:text-text-active',
  '[&_code]:rounded [&_code]:bg-background-secondary [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-text-primary',
  '[&_pre]:rounded-md [&_pre]:bg-background-secondary [&_pre]:p-2'
);

const ASSISTANT_STREAMDOWN_CONTROLS = {
  table: false,
  code: false,
  mermaid: {
    download: true,
    copy: true,
    fullscreen: true,
    panZoom: true,
  },
} as const;

const STREAMING_WORD_FADE: AnimateOptions = {
  animation: 'fadeIn',
  duration: 140,
  easing: 'ease-out',
  maxBacklogMs: 180,
  sep: 'word',
  stagger: 18,
};

// Streamdown adds these itself to draw its caret, but Tailwind does not scan
// node_modules, so they are named here to get generated.
const STREAMING_CARET_CLASS =
  '[&>*:last-child]:after:inline [&>*:last-child]:after:align-baseline [&>*:last-child]:after:content-[var(--streamdown-caret)]';

// The chip resolves its own destination out of markdown attributes the app
// never sees, so the route crosses as `onNavigate` rather than as an href, and
// this renderer is the composition point that binds it.
//
// Bound through the router SINGLETON rather than `useRouter()`, and the reason
// is the component map below: the chip is a markdown tag, so its binding is
// read at click time and never during render. A hook would move that read into
// every render of assistant prose, and three existing suites mount that prose
// without a router — `renderProjectItem` (8 cases), `RoutineCreateGuidedChat`
// (all 13) and `ProjectChat` (1) go red with "NextRouter was not mounted" when
// `useRouter()` is added to `SourceGroundedAssistantMarkdown` below. Reproduce
// with `pnpm test --testPathPattern="(projects|workflows)/"`; a narrower pattern
// misses all three, which is why the count is written down with the suites.
// The port is covered either way — see `chatCiteChipNavigation.seam.test.tsx`.
const navigateToWorkChat: NavigatePort = async (href) => {
  await Router.push(href);
};

const CiteChip = (props: Omit<ChatCiteChipProps, 'onNavigate'>) => (
  <ChatCiteChip {...props} onNavigate={navigateToWorkChat} />
);

// `headerNoWrap` keeps a wide table scrolling inside its clamp rather than
// squeezing its columns: the timeline is a measured surface, and a table must
// not resize it as it streams in.
//
// Built once per density so the map keeps a stable identity across renders —
// Streamdown re-renders its content whenever the `components` reference changes.
const buildAssistantProse = (density: ProseDensity): MarkdownComponents =>
  ({
    ...createResponseProse({ density, headerNoWrap: true }),
    project: CiteChip,
    chat: CiteChip, // legacy
    ref: RefMarkdownElement,
  }) as unknown as MarkdownComponents;

const ASSISTANT_PROSE_BY_DENSITY: Record<ProseDensity, MarkdownComponents> = {
  comfortable: buildAssistantProse('comfortable'),
  compact: buildAssistantProse('compact'),
};

// The two STATIC app-only inputs the source-grounded cluster needs, bound here
// because this module is the only thing that composes it. The third — the
// release flag — is a hook read and is bound at the mount instead, two blocks
// down, for the reason that docblock gives.
//
// `sessionTabs` is the one worth reading twice. The resolver used to name
// `TabType.DOCUMENT` and friends itself, and a VALUE import of a foreign
// domain's inert door is an EDGE rather than a type contribution — which is what
// held four modules app-side. The three members are resolved here and travel as
// data, so `@heidi/session/contracts` stays a type-only contribution behind the
// door. It is a module-level constant rather than a memo because the enum is
// frozen vocabulary: nothing about it varies per render.
const SOURCE_GROUNDED_SESSION_TABS: SourceGroundedSessionTabs = {
  context: TabType.CONTEXT,
  document: TabType.DOCUMENT,
  transcript: TabType.TRANSCRIPT,
};

// The hover-card shell the chip no longer names. `@/components/ui/CitationPopover`
// is shared-UI app code, so it is inverted: the chip renders the trigger and the
// card body, and this composition point wraps them in the real Radix card. The
// props here are the ones the chip used to pass itself, unchanged.
const renderSourceCitationPopover: RenderCitationPopover = ({ trigger, content }) => (
  <CitationPopover openDelay={120}>
    <CitationPopoverTrigger asChild>{trigger}</CitationPopoverTrigger>
    <CitationPopoverContent className="max-h-72 overflow-y-auto p-0">
      {content}
    </CitationPopoverContent>
  </CitationPopover>
);

/**
 * The markdown `<source-cite>` tag, which is the chip plus the one binding that
 * has to be read where the chip MOUNTS rather than where the map is built.
 *
 * `useIsWorkSourceCitationChipsEnabled` is a hook, so reading it in
 * `SourceGroundedAssistantMarkdown` would fire it for every assistant message
 * whether or not the prose carries a citation. Measured, that is not academic:
 * it took `src/features/projects/__tests__/workHomePage.test.tsx` down at 15
 * cases on a partial `@openfeature/react-sdk` mock, because Work home renders
 * assistant prose and no citations. The read belongs at the mount, which is
 * where it sat before the chip crossed the door.
 */
const sourceCiteTagFor = (
  sourceReferences: Map<number, { id: number; label: string; detail?: string }>
) => {
  const Chip = createSourceGroundedCitationComponent(sourceReferences, {
    renderPopover: renderSourceCitationPopover,
    sessionTabs: SOURCE_GROUNDED_SESSION_TABS,
  });
  const SourceCiteTag = (props: {
    children?: ReactNode;
    'data-idx'?: string;
    dataIdx?: string;
  }) => {
    const compactCitationsEnabled = useIsWorkSourceCitationChipsEnabled();
    return <Chip {...props} compactCitationsEnabled={compactCitationsEnabled} />;
  };
  SourceCiteTag.displayName = 'SourceGroundedCitationChipTag';
  return SourceCiteTag;
};

const buildAssistantMdComponents = (
  sourceReferences: Map<number, { id: number; label: string; detail?: string }>,
  density: ProseDensity
): MarkdownComponents => {
  const base = ASSISTANT_PROSE_BY_DENSITY[density];
  if (sourceReferences.size === 0) {
    return base;
  }
  return {
    ...base,
    'source-cite': sourceCiteTagFor(sourceReferences),
  } as unknown as MarkdownComponents;
};

/** Same rule as the chip tag: the footer's flag read sits at its mount. */
const SourceGroundedFooter = ({
  references,
}: {
  references: SourceGroundedReference[];
}) => {
  const compactCitationsEnabled = useIsWorkSourceCitationChipsEnabled();
  return (
    <SourceGroundedReferencesFooter
      references={references}
      compactCitationsEnabled={compactCitationsEnabled}
      sessionTabs={SOURCE_GROUNDED_SESSION_TABS}
    />
  );
};

const SourceGroundedAssistantMarkdown = ({
  grounded,
  streaming,
}: {
  grounded: PreparedSourceGroundedContent;
  streaming: boolean;
}) => {
  const density = useProseDensity();
  const sourceRefMap = useMemo(
    () => referencesById(grounded.inlineReferences),
    [grounded.inlineReferences]
  );
  const mdComponents = useMemo(
    () => buildAssistantMdComponents(sourceRefMap, density),
    [sourceRefMap, density]
  );
  const markdown = prepareAssistantMarkdown(grounded.body, normalizeHeadingDepth, {
    streaming,
  });
  const shouldReduceMotion = useReducedMotion();
  const fadeWords = streaming && !shouldReduceMotion;

  return (
    <>
      <MermaidStreamingContext.Provider value={streaming}>
        <Streamdown
          parseIncompleteMarkdown
          animated={fadeWords ? STREAMING_WORD_FADE : undefined}
          caret={streaming ? 'circle' : undefined}
          isAnimating={streaming}
          mode={!streaming ? 'static' : undefined}
          controls={ASSISTANT_STREAMDOWN_CONTROLS}
          codeBlockMaxHeight={0}
          plugins={streamdownPlugins}
          mermaid={mermaidConfig}
          shikiTheme={shikiTheme}
          rehypePlugins={streamdownRehypePlugins}
          components={mdComponents}
          className={cn(responseProseClass(density), streaming && STREAMING_CARET_CLASS)}
        >
          {markdown}
        </Streamdown>
      </MermaidStreamingContext.Provider>
      {grounded.references.length > 0 && !streaming && (
        <SourceGroundedFooter references={grounded.references} />
      )}
    </>
  );
};

const WORKFLOW_ID_IN_SEED_RE = /workflow_id:\s*([\w-]+)/i;

const SlashRoutineOutcome = ({
  slug,
  isRunning,
  onOpenRoutine,
}: {
  slug: string;
  isRunning: boolean;
  onOpenRoutine?: (workflowId: string) => void;
}) => {
  // The composer owns list fetching; historical rows only observe its shared cache.
  const skills = useAvailableWorkflowSkillsWithWorkflows({ enabled: false });
  const density = useProseDensity();
  const skill = skills.find((item) => item.slug === slug);
  if (!skill) {
    return (
      <div className="flex w-full scroll-mt-2.5 justify-end" data-user-message>
        <div
          className={cn(
            'box-border flex flex-col items-start justify-end gap-1 rounded-lg bg-background-secondary px-2.5 py-2',
            responseQuestionWidthClass(density)
          )}
        >
          <TypographyP
            className={cn(
              'w-full whitespace-pre-wrap text-text-primary [overflow-wrap:anywhere]',
              responseBodyTextClass(density)
            )}
            data-project-chat-transcript-text
          >
            /{slug}
          </TypographyP>
        </div>
      </div>
    );
  }
  return (
    <RoutineOutcomeRow
      action={isRunning ? 'running' : 'ran'}
      name={skill.title}
      Icon={Routines as LucideIcon}
      onOpen={
        skill.kind === 'workflow' && onOpenRoutine
          ? () => onOpenRoutine(skill.skillId)
          : undefined
      }
    />
  );
};

/**
 * A routine invoked mid-chat with extra text persists as the routine's brief,
 * a `User request:` marker, then the clinician's words. When the brief is one
 * of the clinician's routines, show that routine's chip and their words only.
 */
const useRoutineTurnDisplay = (
  parsed: ParsedUserMessage
): ParsedUserMessage & { routineWorkflowId?: string } => {
  const split =
    parsed.routineRunTitle || !parsed.body ? null : splitRoutineTurnBody(parsed.body);
  const routine = useCachedWorkflowByInstructions(split?.brief, reactQueryClient);
  if (!split || !routine) return parsed;
  return {
    ...parsed,
    body: split.request,
    routineRunTitle: routine.name,
    routineWorkflowId: routine.workflow_id,
  };
};

/** The routine a message was sent with, drawn like the composer's routine chip. */
const RoutineChip = ({ title, onOpen }: { title: string; onOpen?: () => void }) => {
  const className =
    'me-1 inline-flex max-w-full items-center gap-1 rounded-md bg-surface px-1.5 align-baseline font-medium text-text-primary shadow-sm';
  const content = (
    <>
      <Routines aria-hidden className="size-3 shrink-0 text-text-secondary" />
      <span className="min-w-0 truncate">{title}</span>
    </>
  );
  if (!onOpen) {
    return (
      <span className={className} data-testid="user-message-routine-chip">
        {content}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(className, 'transition-colors duration-150 hover:bg-surface-hover')}
      data-testid="user-message-routine-chip"
    >
      {content}
    </button>
  );
};

/** Right-aligned user bubble with linked-context chips + attachments. */
export const UserBubble = ({
  text,
  attachments,
  agentRunId,
  animate = true,
  routineRunTitleFallback,
  isRoutineRunSeed = false,
  isRoutineRunning = false,
  routineRunWorkflowId,
  onOpenRoutine,
  launchedRoutine,
}: {
  text: string;
  attachments: AttachmentBlock[];
  agentRunId: string;
  animate?: boolean;
  /**
   * The routine this message was sent with, when the backend stored the
   * routine's seed as its own message (Work home launch). Drawn as the same
   * inline chip the composer showed.
   */
  launchedRoutine?: { title: string; workflowId?: string };
  /** Project title — labels legacy workflow-run seeds that still persist the agent brief. */
  routineRunTitleFallback?: string;
  /** True only for the chat's opening user message — see `ParseUserPreambleOptions.isRoutineRunSeed`. */
  isRoutineRunSeed?: boolean;
  isRoutineRunning?: boolean;
  /** Fallback for a ``Run {name}`` seed, which carries no ``workflow_id``. */
  routineRunWorkflowId?: string;
  onOpenRoutine?: (workflowId: string) => void;
}) => {
  // Above the early returns — this is a hook. Both the bubble's width and its
  // text size come from the same density as the answer below it. The size
  // previously read `sm:text-sm`, which keyed off the VIEWPORT with no base
  // size, so a full-screen question sat at 14px above a 16px answer.
  const density = useProseDensity();
  const parsed = useRoutineTurnDisplay(
    parseUserPreamble(text, { routineRunTitleFallback, isRoutineRunSeed })
  );
  // Shared with ``ProjectTurnTimeline``'s turn-grouping rule so a message
  // that folds into a turn here also doesn't open a phantom group there.
  if (!userMessageRendersBubble(parsed, attachments.length)) {
    return null;
  }

  if (parsed.slashRoutineSlug && !parsed.body && parsed.preamble.length === 0) {
    return (
      <SlashRoutineOutcome
        slug={parsed.slashRoutineSlug}
        isRunning={isRoutineRunning}
        onOpenRoutine={onOpenRoutine}
      />
    );
  }

  if (
    !launchedRoutine &&
    parsed.routineRunTitle &&
    !parsed.body &&
    parsed.preamble.length === 0
  ) {
    const workflowId = text.match(WORKFLOW_ID_IN_SEED_RE)?.[1] ?? routineRunWorkflowId;
    return (
      <RoutineOutcomeRow
        action={isRoutineRunning ? 'running' : 'ran'}
        name={parsed.routineRunTitle}
        Icon={Routines as LucideIcon}
        onOpen={workflowId && onOpenRoutine ? () => onOpenRoutine(workflowId) : undefined}
      />
    );
  }

  const routineChip =
    launchedRoutine ??
    (parsed.routineRunTitle
      ? {
          title: parsed.routineRunTitle,
          workflowId: parsed.routineWorkflowId ?? text.match(WORKFLOW_ID_IN_SEED_RE)?.[1],
        }
      : undefined);
  const routineChipNode = routineChip ? (
    <RoutineChip
      title={routineChip.title}
      onOpen={
        routineChip.workflowId && onOpenRoutine
          ? () => onOpenRoutine(routineChip.workflowId!)
          : undefined
      }
    />
  ) : null;
  const visiblePreamble = parsed.preamble.filter(
    (item) => !isComposerContextPreamble(item)
  );

  return (
    <div className="flex w-full scroll-mt-2.5 justify-end" data-user-message>
      <div
        className={cn(
          'box-border flex flex-col items-start justify-end gap-1 rounded-lg bg-background-secondary px-2.5 py-2',
          responseQuestionWidthClass(density),
          animate && 'duration-300 animate-in fade-in slide-in-from-bottom-1'
        )}
      >
        {routineChipNode && !parsed.body && routineChipNode}
        {visiblePreamble.length > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            {visiblePreamble.map((preItem, i) => {
              const Icon = preItem.kind === 'task' ? ListChecks : FileText;
              return (
                <span
                  key={`${preItem.kind}-${i}-${preItem.name}`}
                  className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 text-xs text-text-secondary"
                >
                  <Icon className="size-3 text-text-tertiary" />
                  <span className="max-w-48 truncate">{preItem.name}</span>
                  {preItem.subtitle && (
                    <span className="text-text-tertiary">· {preItem.subtitle}</span>
                  )}
                </span>
              );
            })}
          </div>
        )}
        {parsed.body && (
          <TypographyP
            className={cn(
              'w-full whitespace-pre-wrap text-text-primary [overflow-wrap:anywhere]',
              responseBodyTextClass(density)
            )}
            data-project-chat-transcript-text
          >
            {routineChipNode}
            {splitRefSegments(parsed.body).map((segment, i) =>
              segment.type === 'text' ? (
                <Fragment key={i}>{segment.text}</Fragment>
              ) : (
                <ChatObjectReference key={i} target={segment.ref} />
              )
            )}
          </TypographyP>
        )}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((file) => (
              <AttachmentBlockRenderer
                key={file.attachment_id}
                attachment={file}
                agentRunId={agentRunId}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/** Assistant prose (markdown), or a chain-marker pill. */
export const AssistantText = ({
  text,
  streaming,
  structuredReferences,
}: {
  text: string;
  streaming: boolean;
  structuredReferences?: SourceGroundedReference[];
}) => {
  const grounded = useMemo(
    () => prepareSourceGroundedContent(text, { streaming, structuredReferences }),
    [text, streaming, structuredReferences]
  );

  if (!text) return null;
  const chainMarker = detectChainMarker(text);
  if (chainMarker) {
    return (
      <StepDoneMarker
        kind={chainMarker.kind}
        outcome={chainMarker.outcome}
        trailing={chainMarker.trailing}
        isStreaming={streaming}
      />
    );
  }

  return (
    <div
      className={ASSISTANT_MARKDOWN_CLASS}
      data-project-chat-transcript-text
      data-source-grounded={grounded.references.length > 0}
    >
      <SourceGroundedAssistantMarkdown grounded={grounded} streaming={streaming} />
    </div>
  );
};

/**
 * Reasoning text for a thinking item — plain muted prose. It used to be a
 * bordered "Thought process" `<details>`; the Working block now owns that job,
 * so a disclosure here only nested one collapse inside another.
 */
export const ThinkingText = ({ text }: { text: string }) => {
  if (!text) return null;
  return (
    <TypographyP2 className="whitespace-pre-wrap break-words text-text-tertiary">
      {text}
    </TypographyP2>
  );
};

/**
 * Destructive row for an errored tool result or a prompt-context failure.
 *
 * ``toolName`` is an optional, already-humanised label for the tool that
 * produced the error. When present it's shown as a bold prefix so a bare
 * backend string (e.g. "session_id is required…") is attributed to the
 * tool that ran ("Generate note from template: session_id is required…"),
 * instead of leaving the user guessing which action failed.
 */
export const ErrorRow = ({
  message,
  toolName,
}: {
  message: string;
  toolName?: string;
}) => (
  <div
    className={cn(
      'flex items-start gap-2 rounded-md border border-validation-destructive-muted bg-validation-destructive-muted px-2.5 py-1.5 text-xs text-validation-destructive',
      'duration-300 animate-in fade-in'
    )}
  >
    <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
    <span className="break-words">
      {toolName ? <span className="font-semibold">{toolName}: </span> : null}
      {message}
    </span>
  </div>
);

/**
 * Neutral "how to continue" note for a stopped-mid-work cancel (AGE-580). A
 * cancel before any output is discarded entirely (no marker). ``label`` arrives
 * already-localized so this leaf stays intl-free.
 */
export const TurnStoppedNote = ({ label }: { label: string }) => (
  <div
    className={cn(
      'inline-flex max-w-full items-center gap-2 self-start rounded-lg border border-border bg-surface py-1.5 pe-3 ps-2.5 text-xs text-text-secondary',
      'duration-300 animate-in fade-in'
    )}
    data-testid="turn-stopped-note"
  >
    <CircleStop className="size-4 shrink-0 text-text-tertiary" />
    <span className="min-w-0 break-words">{label}</span>
  </div>
);

/**
 * Inline "tool used" row for non-desktop tools (web search, library
 * lookup, etc.). Mirrors Evidence's `SearchBlock`: caption label +
 * one-line input preview + status icon.
 *
 * UNUSED — no production consumer (the live path uses `InlineToolCallRow`).
 * Do NOT re-adopt as-is: it labels with the raw `toolName`. If revived, take a
 * `toolCallDisplay` and render `toolCallDisplay?.title ?? toolName`, like the
 * live rows, so it never surfaces a bare snake_case tool name.
 */
export const ToolCallRow = ({
  toolName,
  toolInput,
  status,
  onOpen,
  openLabel,
  openTestId,
}: {
  toolName: string;
  toolInput: Record<string, unknown>;
  status: BlockStatus;
  /** When set, the whole row becomes a button that opens the artifact. */
  onOpen?: () => void;
  /** Trailing affordance label, e.g. "Open patient". */
  openLabel?: string;
  openTestId?: string;
}) => {
  const label = toolName;
  const preview = previewToolInput(toolInput);
  const isRunning = status === 'running';
  const isError = status === 'error';
  const isCancelled = status === 'cancelled';
  const isOpenable = !!onOpen;

  const content = (
    <>
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-300',
          isError
            ? 'bg-validation-destructive-muted text-validation-destructive'
            : isRunning
              ? 'bg-background-tertiary text-text-secondary'
              : isCancelled
                ? 'bg-background-tertiary text-text-secondary'
                : 'bg-validation-success-muted text-validation-success'
        )}
      >
        {isRunning ? (
          <Loader2 className="size-4 animate-spin" />
        ) : isError ? (
          <AlertTriangle className="size-4" />
        ) : isCancelled ? (
          <Ban className="size-4" />
        ) : (
          <CheckCircle2 className="size-4 motion-safe:duration-300 motion-safe:animate-in motion-safe:zoom-in-50" />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <TypographyP2 className="truncate font-semibold leading-tight text-text-primary">
          {label}
        </TypographyP2>
        {preview && (
          <TypographyCaption className="truncate leading-tight text-text-tertiary">
            {preview}
          </TypographyCaption>
        )}
      </span>
      {isOpenable && (
        <span className="flex shrink-0 items-center gap-1 text-text-secondary transition-colors duration-200 group-hover/tool:text-text-primary">
          {openLabel && (
            <span className="text-xs font-medium max-sm:hidden">{openLabel}</span>
          )}
          <ChevronRight className="size-4 transition-transform duration-200 ease-out group-hover/tool:translate-x-0.5 rtl:-scale-x-100" />
        </span>
      )}
    </>
  );

  const shell = cn(
    'group/tool flex w-full min-w-0 items-center gap-3 rounded-lg border border-border bg-surface-accent px-3.5 py-3 text-left',
    'duration-300 animate-in fade-in slide-in-from-bottom-1',
    isError && 'border-validation-destructive-border bg-validation-destructive-muted/40',
    isOpenable &&
      'cursor-pointer transition-all duration-200 hover:-translate-y-px hover:border-border-hover hover:shadow-chat focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus active:translate-y-0 active:scale-[0.997]'
  );

  if (isOpenable) {
    return (
      <button type="button" onClick={onOpen} className={shell} data-testid={openTestId}>
        {content}
      </button>
    );
  }
  return <div className={shell}>{content}</div>;
};

/** "Open <artifact>" affordance shown next to a successful artifact-producing tool row. */
export const ArtifactOpenButton = ({
  artifact,
  onOpen,
}: {
  artifact: Artifact;
  onOpen: (artifact: Artifact) => void;
}) => {
  const intl = useIntl();
  return (
    <div className="flex w-full">
      <Button
        size="sm"
        variant="outline"
        onClick={() => onOpen(artifact)}
        data-testid={`open-artifact-${artifact.type}`}
        className="ms-auto transition-transform active:scale-95"
      >
        <PanelRight className="me-2 size-3.5" />
        {artifactOpenLabel(intl, artifact)}
      </Button>
    </div>
  );
};

const ClarificationAnswerPlain = ({
  clarification,
}: {
  clarification: ResolvedClarification;
}) => {
  if (clarification.isDismissed) {
    return (
      <span className="text-sm text-text-primary">
        <FormattedMessage {...m.skipped} />
      </span>
    );
  }
  if (clarification.answer) {
    return (
      <span className="whitespace-pre-line break-words text-sm text-text-primary">
        {clarification.answer}
      </span>
    );
  }
  return (
    <span className="text-sm text-text-primary">
      <FormattedMessage {...m.answered} />
    </span>
  );
};

/** Fetches session meta only when the answer is a bare pick_session id. */
const ClarificationAnswerSessionLabel = ({ sessionId }: { sessionId: string }) => {
  const {
    data: meta,
    isLoading,
    isError,
  } = useQuerySessionById({
    sessionId,
    select: (session) => (session ? readSessionAnswerMeta(session) : undefined),
  });

  // ``isLoading`` (pending && fetching) — not ``isPending``. A disabled
  // TanStack Query v5 query stays pending forever with no fetch; treating
  // that as loading would spin forever (no token / haiku / offline local).
  if (isLoading && !meta) {
    return <SessionMetaRow as="div" status="loading" bordered={false} />;
  }

  if (isError || !meta) {
    return <SessionMetaRow as="div" status="unavailable" bordered={false} />;
  }

  return (
    <SessionMetaRow
      as="div"
      sessionName={meta.sessionName}
      patientName={meta.patientName}
      bordered={false}
    />
  );
};

const ClarificationAnswerText = ({
  clarification,
}: {
  clarification: ResolvedClarification;
}) => {
  if (clarification.unresolvedSessionId) {
    return (
      <ClarificationAnswerSessionLabel sessionId={clarification.unresolvedSessionId} />
    );
  }
  if (clarification.unresolvedTemplateId) {
    return (
      <ClarificationAnswerTemplateLabel templateId={clarification.unresolvedTemplateId} />
    );
  }
  return <ClarificationAnswerPlain clarification={clarification} />;
};

/**
 * Summary rendered in place of the default tool rows for one or more
 * consecutive resolved ``ask_user`` clarifications. The active card lives
 * in the composer dock (see `ProjectChat` → `InlineClarificationBlock`);
 * once a question is answered or dismissed the transcript collapses the
 * run into a single "Answers" card that lists each question with the
 * answer the user gave (or a "Skipped" marker when they dismissed it).
 */
export const ResolvedClarificationsCard = ({
  clarifications,
}: {
  clarifications: ResolvedClarification[];
}) => {
  const intl = useIntl();
  if (clarifications.length === 0) return null;
  return (
    <div
      className={cn(
        'flex w-full flex-col gap-3 rounded-xl border border-border bg-surface p-4',
        'duration-300 animate-in fade-in'
      )}
      data-testid="clarification-answers"
    >
      <TypographyCaption className="font-medium text-text-tertiary">
        <FormattedMessage {...m.answersHeading} />
      </TypographyCaption>
      <div className="flex flex-col gap-3">
        {clarifications.map((clarification) => (
          <div
            key={clarification.id}
            className="flex flex-col gap-0.5"
            data-testid="clarification-answer"
          >
            <span className="break-words text-sm text-text-secondary">
              {clarification.question ??
                intl.formatMessage(m.clarificationQuestionFallback)}
            </span>
            <ClarificationAnswerText clarification={clarification} />
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Compact "step complete" row used between subagent calls so the user
 * sees the chain advancing without scrolling through wordy prose.
 */
const StepDoneMarker = ({
  kind,
  outcome,
  trailing,
  isStreaming,
}: {
  kind: 'step' | 'final';
  outcome: string | undefined;
  trailing?: string;
  isStreaming: boolean;
}) => {
  const isFinal = kind === 'final';
  const trailingGrounded = useMemo(
    () =>
      trailing
        ? prepareSourceGroundedContent(trailing, { streaming: isStreaming })
        : { body: '', references: [], inlineReferences: [] },
    [trailing, isStreaming]
  );
  return (
    <div
      className={cn(
        'flex w-full flex-col gap-2',
        'duration-300 animate-in fade-in slide-in-from-bottom-1'
      )}
      data-chain-marker={kind}
    >
      <div
        className={cn(
          'inline-flex items-start gap-2 rounded-full border px-3 py-1.5 text-xs',
          isFinal
            ? 'border-validation-success/30 bg-validation-success-muted text-validation-success'
            : 'border-border bg-background-secondary text-text-primary'
        )}
      >
        {isFinal ? (
          <CircleCheckBig className="mt-px size-3.5 shrink-0" />
        ) : (
          <CheckCircle2 className="mt-px size-3.5 shrink-0 text-validation-success" />
        )}
        <span className="min-w-0 break-words leading-snug">
          {outcome ? (
            <FormattedMessage
              {...(isFinal ? m.allDoneWithOutcome : m.doneWithOutcome)}
              values={{
                outcome,
                label: (chunks) => <span className="font-semibold">{chunks}</span>,
                outcomeStyle: (chunks) => (
                  <span className={cn(isFinal && 'font-medium')}>{chunks}</span>
                ),
              }}
            />
          ) : (
            <span className="font-semibold">
              <FormattedMessage {...(isFinal ? m.allDone : m.done)} />
            </span>
          )}
        </span>
      </div>
      {trailing && (
        <div
          className={ASSISTANT_MARKDOWN_CLASS}
          data-project-chat-transcript-text
          data-source-grounded={trailingGrounded.references.length > 0}
        >
          <SourceGroundedAssistantMarkdown
            grounded={trailingGrounded}
            streaming={isStreaming}
          />
        </div>
      )}
    </div>
  );
};

type ChainMarker = {
  kind: 'step' | 'final';
  outcome: string | undefined;
  trailing?: string;
};

/**
 * Match the orchestrator's chain markers:
 *   ✅ Done: <outcome>      → per-step confirmation pill
 *   ✅ All done.[ <summary>] → final wrap-up pill
 */
const detectChainMarker = (raw: string): ChainMarker | undefined => {
  const text = raw.trim();
  if (!text) return undefined;
  const firstNewline = text.indexOf('\n');
  const firstLine = (firstNewline === -1 ? text : text.slice(0, firstNewline)).trim();
  const trailing =
    firstNewline === -1 ? undefined : text.slice(firstNewline + 1).trim() || undefined;

  const stepMatch = firstLine.match(/^✅\s*Done\s*[:\-—]\s*(.+)$/i);
  if (stepMatch && stepMatch[1]) {
    return { kind: 'step', outcome: stepMatch[1].trim(), trailing };
  }
  const finalMatch = firstLine.match(/^✅\s*All done\.?\s*(.*)$/i);
  if (finalMatch) {
    const tail = finalMatch[1]?.trim();
    return { kind: 'final', outcome: tail || undefined, trailing };
  }
  return undefined;
};

/**
 * Best-effort one-line preview of a tool's input. Surfaces a human-friendly
 * field when present; otherwise nothing — we never dump raw machine input
 * (e.g. a bare `session_id`) into the clinician-facing transcript. (AGE-435)
 */
const previewToolInput = (input: Record<string, unknown>): string | undefined => {
  for (const key of ['query', 'q', 'search', 'url', 'title', 'prompt', 'name'] as const) {
    const value = input[key];
    if (typeof value === 'string' && value.trim()) return truncate(value.trim(), 120);
  }
  return undefined;
};

const truncate = (s: string, max: number): string =>
  s.length <= max ? s : `${s.slice(0, max - 1)}…`;
